import { computed } from '@angular/core';
import { ID } from '@clean-code/models/common';
import {
  patchState,
  signalStore,
  type,
  withComputed,
  withMethods,
  withState,
} from '@ngrx/signals';
import {
  EntityId,
  removeEntity,
  SelectEntityId,
  setAllEntities,
  setEntity,
  updateEntity,
  upsertEntity,
  withEntities,
} from '@ngrx/signals/entities';
import { NormalizedMarketDashboardResponse } from '../models/market-dashboard-response';

type MarketDashboardColumn =
  NormalizedMarketDashboardResponse['columns'][number];
type MarketDashboardDetailColumn =
  NormalizedMarketDashboardResponse['detailColumns'][number];
type MarketDashboardGroup = NormalizedMarketDashboardResponse['groups'][number];
type MarketDashboardWidget =
  NormalizedMarketDashboardResponse['widgets'][number];

type MarketDashboardSignalState = {
  activeDashboard: NormalizedMarketDashboardResponse['dashboard'] | null;
  editMode: boolean;
};

const initialState: MarketDashboardSignalState = {
  activeDashboard: null,
  editMode: false,
};

const selectId: SelectEntityId<{ id: ID | undefined }> = (entity) =>
  entity.id as EntityId;

const byPosition = <Entity extends { position?: number }>(
  left: Entity,
  right: Entity,
): number => (left.position ?? 0) - (right.position ?? 0);

export const MarketDashboardSignalStore = signalStore(
  withState(initialState),
  withEntities({
    entity: type<MarketDashboardColumn>(),
    collection: 'columns',
  }),
  withEntities({
    entity: type<MarketDashboardDetailColumn>(),
    collection: 'detailColumns',
  }),
  withEntities({
    entity: type<MarketDashboardGroup>(),
    collection: 'groups',
  }),
  withEntities({
    entity: type<MarketDashboardWidget>(),
    collection: 'widgets',
  }),
  withComputed(({ activeDashboard, columnsEntities }) => ({
    activeDashboardId: computed(() => activeDashboard()?.id ?? null),
    columnsForActiveDashboard: computed(() => {
      const dashboardId = activeDashboard()?.id;

      return columnsEntities()
        .filter((column) => column.dashboardId === dashboardId)
        .sort(byPosition);
    }),
    activeDashboardColumnCount: computed(
      () =>
        columnsEntities().filter(
          (column) => column.dashboardId === activeDashboard()?.id,
        ).length,
    ),
  })),
  withMethods((store) => ({
    upsertColumn(column: MarketDashboardColumn): void {
      const existingColumn = store.columnsEntityMap()[column.id as EntityId];
      const storedColumn = { ...column };

      if (storedColumn.extended === undefined) {
        delete storedColumn.extended;

        if (existingColumn?.extended !== undefined) {
          storedColumn.extended = existingColumn.extended;
        }
      }

      patchState(
        store,
        upsertEntity(storedColumn, {
          collection: 'columns',
          selectId,
        }),
      );
    },
    removeColumn(id: ID): void {
      patchState(
        store,
        removeEntity(id as EntityId, { collection: 'columns' }),
      );
    },
    upsertDetailColumn(detailColumn: MarketDashboardDetailColumn): void {
      patchState(
        store,
        setEntity(
          { ...detailColumn },
          {
            collection: 'detailColumns',
            selectId,
          },
        ),
      );
    },
    removeDetailColumn(id: ID): void {
      patchState(
        store,
        removeEntity(id as EntityId, { collection: 'detailColumns' }),
      );
    },
    upsertGroup(group: MarketDashboardGroup): void {
      const storedGroup = {
        ...group,
        settings: group.settings ? { ...group.settings } : group.settings,
      } as MarketDashboardGroup & { items?: unknown };

      delete storedGroup.items;

      patchState(
        store,
        upsertEntity(storedGroup, {
          collection: 'groups',
          selectId,
        }),
      );
    },
    removeGroup(id: ID): void {
      patchState(store, removeEntity(id as EntityId, { collection: 'groups' }));
    },
    upsertWidget(widget: MarketDashboardWidget): void {
      patchState(
        store,
        upsertEntity(
          { ...widget },
          {
            collection: 'widgets',
            selectId,
          },
        ),
      );
    },
    removeWidget(id: ID): void {
      patchState(
        store,
        removeEntity(id as EntityId, { collection: 'widgets' }),
      );
    },
    toggleColumnExpanded(id: ID): void {
      const column = store.columnsEntityMap()[id as EntityId];

      if (!column) {
        return;
      }

      patchState(
        store,
        updateEntity(
          {
            id: id as EntityId,
            changes: { extended: !column.extended },
          },
          { collection: 'columns', selectId },
        ),
      );
    },
    replace(normalized: NormalizedMarketDashboardResponse): void {
      patchState(
        store,
        { activeDashboard: normalized.dashboard },
        setAllEntities(normalized.columns, {
          collection: 'columns',
          selectId,
        }),
        setAllEntities(normalized.detailColumns, {
          collection: 'detailColumns',
          selectId,
        }),
        setAllEntities(normalized.groups, {
          collection: 'groups',
          selectId,
        }),
        setAllEntities(normalized.widgets, {
          collection: 'widgets',
          selectId,
        }),
      );
    },
    reset(): void {
      patchState(
        store,
        { activeDashboard: initialState.activeDashboard },
        setAllEntities([], { collection: 'columns', selectId }),
        setAllEntities([], { collection: 'detailColumns', selectId }),
        setAllEntities([], { collection: 'groups', selectId }),
        setAllEntities([], { collection: 'widgets', selectId }),
      );
    },
    setEditMode(editMode: boolean): void {
      patchState(store, { editMode });
    },
    toggleEditMode(): void {
      patchState(store, ({ editMode }) => ({ editMode: !editMode }));
    },
  })),
);
